/**
 * PropertyValuations - Valuation History Display
 * 
 * Shows the valuation history of a property with:
 * - Timeline visualization
 * - Value trends
 * - Valuation details
 */

'use client';

import type { Valuation } from '@/lib/api/clients/properties';
import { TrendingUp, TrendingDown, Minus, Calendar, Info } from 'lucide-react';

export interface PropertyValuationsProps {
  valuations: Valuation[];
  locale?: string;
  className?: string;
  messages?: {
    title?: string;
    subtitle?: string;
    noValuations?: string;
    method?: string;
    purpose?: string;
    confidence?: string;
    valuationDate?: string;
    notes?: string;
    latestValuation?: string;
    valuationHistory?: string;
  };
}

export function PropertyValuations({
  valuations,
  locale = 'en',
  className = '',
  messages = {},
}: PropertyValuationsProps) {
  const {
    title = 'Property Valuations',
    subtitle = 'Historical and current property valuations',
    method = 'Method',
    purpose = 'Purpose',
    confidence = 'Confidence',
    latestValuation = 'Latest Valuation',
    valuationHistory = 'Valuation History',
  } = messages;

  if (valuations.length === 0) {
    return null;
  }

  // Sort by date descending
  const sortedValuations = [...valuations].sort(
    (a, b) => new Date(b.valuationDate).getTime() - new Date(a.valuationDate).getTime()
  );

  const latestVal = sortedValuations[0];
  const previousVal = sortedValuations[1];

  if (!latestVal) return null;

  // Calculate trend
  let trend: 'up' | 'down' | 'neutral' = 'neutral';
  let trendPercentage: number | null = null;
  
  if (previousVal) {
    const diff = latestVal.value - previousVal.value;
    trendPercentage = (diff / previousVal.value) * 100;
    trend = diff > 0 ? 'up' : diff < 0 ? 'down' : 'neutral';
  }

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const methodLabels: Record<string, string> = {
    COMPARATIVE: 'Comparative Market Analysis',
    INCOME: 'Income Approach',
    COST: 'Cost Approach',
    AUTOMATED: 'Automated Valuation',
    DESKTOP: 'Desktop Valuation',
    FULL: 'Full Appraisal',
  };

  const purposeLabels: Record<string, string> = {
    SALE: 'Sale',
    PURCHASE: 'Purchase',
    REFINANCE: 'Refinance',
    INSURANCE: 'Insurance',
    TAX: 'Tax Assessment',
    ESTATE: 'Estate Planning',
    PORTFOLIO: 'Portfolio Review',
  };

  const confidenceColors: Record<string, string> = {
    HIGH: 'bg-green-100 text-green-800',
    MEDIUM: 'bg-yellow-100 text-yellow-800',
    LOW: 'bg-red-100 text-red-800',
  };

  return (
    <section className={className}>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
        <p className="text-gray-600 mt-1">{subtitle}</p>
      </div>

      {/* Latest Valuation Card */}
      <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl p-6 mb-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-gray-600 uppercase tracking-wide">{latestValuation}</p>
            <p className="text-4xl font-bold text-gray-900 mt-2">
              {formatCurrency(latestVal.value, latestVal.currency)}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {formatDate(latestVal.valuationDate)}
            </p>
          </div>
          
          {trendPercentage !== null && (
            <div className={`flex items-center gap-1 px-3 py-1.5 rounded-full ${
              trend === 'up' ? 'bg-green-100 text-green-700' :
              trend === 'down' ? 'bg-red-100 text-red-700' :
              'bg-gray-100 text-gray-700'
            }`}>
              {trend === 'up' && <TrendingUp className="w-4 h-4" />}
              {trend === 'down' && <TrendingDown className="w-4 h-4" />}
              {trend === 'neutral' && <Minus className="w-4 h-4" />}
              <span className="text-sm font-medium">
                {trendPercentage > 0 ? '+' : ''}{trendPercentage.toFixed(1)}%
              </span>
            </div>
          )}
        </div>

        {/* Details */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-4 border-t border-primary/10">
          <div>
            <p className="text-xs text-gray-500 uppercase">{method}</p>
            <p className="text-sm font-medium text-gray-900 mt-1">
              {methodLabels[latestVal.valuationMethod] || latestVal.valuationMethod}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase">{purpose}</p>
            <p className="text-sm font-medium text-gray-900 mt-1">
              {purposeLabels[latestVal.valuationPurpose] || latestVal.valuationPurpose}
            </p>
          </div>
          {latestVal.confidence && (
            <div>
              <p className="text-xs text-gray-500 uppercase">{confidence}</p>
              <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full ${
                confidenceColors[latestVal.confidence] || 'bg-gray-100 text-gray-800'
              }`}>
                {latestVal.confidence}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Valuation History Timeline */}
      {sortedValuations.length > 1 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{valuationHistory}</h3>
          
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
            
            <div className="space-y-4">
              {sortedValuations.map((val, index) => (
                <div key={val.id} className="relative pl-10">
                  {/* Timeline dot */}
                  <div className={`absolute left-2 w-4 h-4 rounded-full border-2 ${
                    index === 0 
                      ? 'bg-primary border-primary' 
                      : 'bg-white border-gray-300'
                  }`} style={{ top: '6px' }} />
                  
                  <div className={`p-4 rounded-lg border ${
                    index === 0 
                      ? 'bg-white border-primary/20 shadow-sm' 
                      : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-lg font-bold text-gray-900">
                          {formatCurrency(val.value, val.currency)}
                        </p>
                        <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(val.valuationDate)}</span>
                        </div>
                      </div>
                      
                      <div className="text-right text-sm">
                        <p className="text-gray-600">
                          {methodLabels[val.valuationMethod] || val.valuationMethod}
                        </p>
                        <p className="text-gray-500">
                          {purposeLabels[val.valuationPurpose] || val.valuationPurpose}
                        </p>
                      </div>
                    </div>
                    
                    {val.notes && (
                      <div className="flex items-start gap-2 mt-3 pt-3 border-t border-gray-100">
                        <Info className="w-4 h-4 text-gray-400 mt-0.5" />
                        <p className="text-sm text-gray-600">{val.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default PropertyValuations;
